import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { ArrowUpRight, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/store/auth.store';
import {
  SETTINGS_DESTINATIONS,
  SETTINGS_CATEGORY_ORDER,
  isSettingsTileVisible,
  type SettingsCategory,
  type SettingsDestination,
} from '@/components/layout/Sidebar';

const SUPPORT_EMAIL = 'support@verigrc.com';

// Per-category icon-chip hue + hover accent, mapped to existing accent tokens
// (no hardcoded design hex/oklch). Literal class strings so Tailwind's JIT emits them.
const CATEGORY_STYLE: Record<SettingsCategory, { chip: string; hoverBorder: string }> = {
  'Account & Organization':    { chip: 'bg-accent-cyan-dim text-accent-cyan',       hoverBorder: 'group-hover:border-accent-cyan/50' },
  'Users & Access':            { chip: 'bg-accent-sky-dim text-accent-sky',         hoverBorder: 'group-hover:border-accent-sky/50' },
  'Security & Assurance':      { chip: 'bg-accent-emerald-dim text-accent-emerald', hoverBorder: 'group-hover:border-accent-emerald/50' },
  'Notifications & Escalation':{ chip: 'bg-accent-amber-dim text-accent-amber',     hoverBorder: 'group-hover:border-accent-amber/50' },
  'Integrations & Automation': { chip: 'bg-accent-violet-dim text-accent-violet',   hoverBorder: 'group-hover:border-accent-violet/50' },
  'Support':                   { chip: 'bg-accent-rose-dim text-accent-rose',       hoverBorder: 'group-hover:border-accent-rose/50' },
};

function SettingsTile({ dest }: { dest: SettingsDestination }) {
  const Icon = dest.icon;
  const style = CATEGORY_STYLE[dest.category];

  const body = (
    <>
      <div className={cn('flex h-11 w-11 shrink-0 items-center justify-center rounded-xl', style.chip)}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-text-primary">{dest.label}</p>
        {dest.description && (
          <p className="mt-1 text-xs leading-relaxed text-text-secondary">{dest.description}</p>
        )}
      </div>
    </>
  );

  // Coming-soon: non-navigable, muted, dashed border + badge. No link, no route.
  if (dest.comingSoon) {
    return (
      <div className="relative flex items-start gap-3.5 rounded-2xl border border-dashed border-border-subtle bg-bg-surface/60 p-5 opacity-70">
        {body}
        <span className="absolute right-3.5 top-3.5 rounded-full border border-border-subtle bg-bg-surface-2 px-2 py-0.5 text-[10px] font-semibold text-text-muted">
          Coming soon
        </span>
      </div>
    );
  }

  return (
    <NavLink
      to={dest.href!}
      className={cn(
        'group relative flex items-start gap-3.5 rounded-2xl border border-border-subtle bg-bg-surface p-5 transition-all duration-200',
        'hover:-translate-y-[3px] hover:bg-bg-surface-2 hover:shadow-lg',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-cyan',
        style.hoverBorder,
      )}
    >
      {body}
      <ArrowUpRight className="absolute right-4 top-4 h-4 w-4 text-text-secondary/30 transition-colors group-hover:text-text-secondary/70" />
    </NavLink>
  );
}

/**
 * All Settings hub — every settings destination (SMTP excluded) grouped into 6
 * categories. Hide-only by tile role: a category header is hidden when it has zero
 * visible tiles. A header search box filters the role-visible tiles by label +
 * description. Single source of truth = SETTINGS_DESTINATIONS.
 */
export function SettingsHubPage() {
  const { hasAnyRole, entitledModules } = useAuthStore();
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();

  const groups = SETTINGS_CATEGORY_ORDER.map(category => ({
    category,
    tiles: SETTINGS_DESTINATIONS.filter(
      d =>
        d.category === category &&
        isSettingsTileVisible(d, hasAnyRole, entitledModules) &&
        (q === '' || `${d.label} ${d.description ?? ''}`.toLowerCase().includes(q)),
    ),
  })).filter(group => group.tiles.length > 0);

  const totalMatches = groups.reduce((n, g) => n + g.tiles.length, 0);

  return (
    <div className="mx-auto max-w-6xl space-y-9">
      {/* Header + search */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-text-secondary">Settings</div>
          <h1 className="mt-2 text-2xl font-bold text-text-primary">All Settings</h1>
          <p className="mt-1 text-sm text-text-secondary">Manage your account, team, security, and integrations.</p>
        </div>
        <label className="flex w-full items-center gap-2 rounded-xl border border-border-subtle bg-bg-surface-2 px-3 py-2 transition-colors focus-within:border-accent-cyan sm:w-72">
          <Search className="h-4 w-4 shrink-0 text-text-secondary" />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search settings"
            aria-label="Search settings"
            className="w-full min-w-0 bg-transparent text-sm text-text-primary placeholder:text-text-secondary focus:outline-none"
          />
        </label>
      </div>

      {totalMatches === 0 ? (
        <div className="rounded-2xl border border-border-subtle bg-bg-surface p-10 text-center text-sm text-text-secondary">
          No settings match “<span className="text-text-primary">{query.trim()}</span>”.
        </div>
      ) : (
        groups.map(({ category, tiles }) => (
          <section key={category} className="space-y-4">
            <div className="flex items-center gap-3">
              <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-text-secondary">{category}</span>
              <span className="rounded-full border border-border-subtle bg-bg-surface px-2 py-px text-[11px] font-semibold text-text-muted">
                {tiles.length}
              </span>
              <span className="h-px flex-1 bg-gradient-to-r from-border-subtle to-transparent" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {tiles.map(tile => (
                <SettingsTile key={tile.href ?? tile.label} dest={tile} />
              ))}
            </div>
          </section>
        ))
      )}

      <div className="border-t border-border-subtle pt-5 text-sm text-text-secondary">
        Need help? Contact{' '}
        <a href={`mailto:${SUPPORT_EMAIL}`} className="font-medium text-accent-cyan hover:underline">
          {SUPPORT_EMAIL}
        </a>
        .
      </div>
    </div>
  );
}
